import { useState, useRef } from 'react';
import type { ChartWrapperRef } from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import KeyMetricsChart from './components/KeyMetricsChart';
import KeyMetricsTabs from './components/KeyMetricsTabs';
import { Grid, Toggle, Button } from '@simplywallst/ui-core';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';
import { useTracking } from 'react-tracking';
import {
  ToggleWrapper,
  ToggleLabel,
  MultiplesWrapper,
  OthersWrapper,
} from './styled';
import Tooltip from '@/components/Tooltip';
import KeyMetricsTable from './components/KeyMetricsTable';
import KeyMetricsStatements from './components/KeyMetricsStatements';
import KeyMetricsOthers from './components/KeyMetricsOthers';
import {
  useValuationTranslation,
  ValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { useBaseMetricLabel } from './hooks';
import { SWS_HOST } from '@/constants';

type OptionalTooltipProps = {
  title: string;
  show: boolean;
  children: React.ReactNode;
};

const OptionalTooltip = ({ title, show, children }: OptionalTooltipProps) => {
  if (show) {
    return (
      <Tooltip openDelay={300} light title={<span>{title}</span>}>
        <>{children}</>
      </Tooltip>
    );
  }

  return <>{children}</>;
};

const HelpContent = () => {
  return (
    <div>
      <ValuationTranslation i18nKey="keyMetric.help.description">
        <p>
          All valuation metrics suit some stocks better than others. We choose
          the most relevant data point to use for our relative valuation.
        </p>
        <p>
          If a company is profitable, we use the Price to Earnings (PE) ratio,
          which measures how much investors are paying for today’s earnings. A
          high PE ratio may indicate that investors are expecting high growth in
          the future. This is calculated by dividing the share price by earnings
          per share.
        </p>
        <p>
          If a company is not yet making a profit, we use the Price to Sales
          (PS) ratio, which measures how much investors are paying for today’s
          sales or revenues. This is calculated by dividing the share price by
          revenue per share.
        </p>
        <p>
          For some companies like banks or real estate investors, Price to Book
          (PB) is a useful metric. It looks at what the value of the balance
          sheet is compared to the market capitalisation of the company. This is
          calculated by dividing the market capitalisation by the book value of
          the company.
        </p>
        <p>
          For more information on how we select the key valuation metrics,
          please check out our
          <Button
            as="a"
            target="_blank"
            href={`${SWS_HOST}/help/articles/hc/en-us/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report?section=Selection-of-Preferred-Multiple`}
            rel="noopener noreferrer"
            styleType="strongLink"
          >
            Help Centre.
          </Button>
        </p>
      </ValuationTranslation>
    </div>
  );
};

export interface Multiple {
  name: string;
  fullName: string;
  value: string;
}

export const KeyValuationMetrics = () => {
  const { trackEvent } = useTracking();
  const ChartWrapperRef = useRef<ChartWrapperRef>(null);

  const selected = useValuationData();
  const {
    multiples: { preferredMultiple, secondMultiple, thirdMultiple },
  } = selected;

  const [selectedMultiple, setSelectedMultiple] =
    useState<Multiple>(preferredMultiple);
  const [selectedForward, setSelectedForward] =
    useState<Multiple>(preferredMultiple);
  const [toggleForward, setToggleForward] = useState(false);

  const handleTabSelect = (multiple: Multiple) => {
    setSelectedMultiple(multiple);
    ChartWrapperRef.current?.showChart(true); //TODO: Still some sort of flickering here
    setSelectedForward(multiple);
    setToggleForward(false);

    trackEvent({
      subject: 'valuation',
      action: 'click',
      modifier: `key_value_metric_${
        multiple.name === 'n/a' ? 'others' : multiple.name
      }`,
    });
  };

  const disableToggle =
    (selectedMultiple.name === 'PE' && selected.forwardPE === 'n/a') ||
    (selectedMultiple.name === 'PS' && selected.forwardPS === 'n/a');

  const getBaseMetricLabel = useBaseMetricLabel();

  const t = useValuationTranslation();

  return (
    <Grid
      templateColumnsMd="45% auto"
      templateColumnsLg="35% auto"
      gapMd="20px"
    >
      <Grid alignContent="start">
        <KeyMetricsTabs
          selectedMultiple={selectedMultiple}
          preferredMultiple={preferredMultiple}
          secondMultiple={secondMultiple}
          thirdMultiple={thirdMultiple}
          handleSelect={handleTabSelect}
        />
        <KeyMetricsStatements multiple={selectedMultiple} />
      </Grid>
      <Grid>
        <MultiplesWrapper hidden={selectedMultiple.name === 'n/a'}>
          <ChartWrapper
            ref={ChartWrapperRef}
            id="key-value-metrics"
            chart={
              <KeyMetricsChart
                metric={
                  selectedForward.name === 'n/a'
                    ? selectedMultiple.name
                    : selectedForward.name
                }
                value={
                  selectedForward.name === 'n/a'
                    ? selectedMultiple.value
                    : selectedForward.value
                }
              />
            }
            help={<HelpContent />}
            helpTitle={t('keyMetric.help.title')}
            feedbackLabel={t('keyMetric.feedback')}
            moreActions={
              selectedMultiple.name !== 'PB' && (
                <OptionalTooltip
                  show={selectedMultiple.name !== 'PB' && disableToggle}
                  title={t('keyMetric.noForecastData', {
                    metric: selectedMultiple.name,
                    baseMetricLabel: getBaseMetricLabel(
                      selected.metricsData[selectedMultiple.name]?.label
                    ),
                  })}
                >
                  <ToggleWrapper>
                    <Toggle
                      disabled={disableToggle}
                      name="Forward Multiple"
                      label={
                        <ToggleLabel>
                          {selectedMultiple.name === 'PE' &&
                            t('keyMetric.forwardPE')}
                          {selectedMultiple.name === 'PS' &&
                            t('keyMetric.forwardPS')}
                        </ToggleLabel>
                      }
                      value={toggleForward}
                      onChange={() => {
                        setSelectedForward(
                          toggleForward
                            ? preferredMultiple
                            : {
                                ...selectedMultiple,
                                name:
                                  selectedMultiple.name === 'PE'
                                    ? 'Forward PE'
                                    : 'Forward PS',
                                value:
                                  selectedMultiple.name === 'PE'
                                    ? selected.forwardPE
                                    : selected.forwardPS,
                              }
                        );
                        setToggleForward((toggleForward) => !toggleForward);
                      }}
                    />
                  </ToggleWrapper>
                </OptionalTooltip>
              )
            }
            table={
              <KeyMetricsTable
                metric={
                  selectedForward.name === 'n/a'
                    ? selectedMultiple.name
                    : selectedForward.name
                }
                value={
                  selectedForward.name === 'n/a'
                    ? selectedMultiple.value
                    : selectedForward.value
                }
              />
            }
            preRenderHeight={280}
          ></ChartWrapper>
        </MultiplesWrapper>
        <OthersWrapper
          data-cy-id="key-value-metrics-others"
          hidden={selectedMultiple.name !== 'n/a'}
        >
          <KeyMetricsOthers />
        </OthersWrapper>
      </Grid>
    </Grid>
  );
};
